export default function VehicleDetailSkeleton() {
  return (
    <div
      className="min-h-screen bg-white"
      aria-busy="true"
      aria-label="Loading vehicle details"
    >
      {/* Visually-hidden loading announcement for screen readers */}
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        Loading vehicle details, please wait…
      </p>

      {/* Breadcrumb Skeleton */}
      <div className="bg-[#F7F5F0] border-b border-[#E8E2D6]" aria-hidden="true">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2">
            <div className="h-4 w-12 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
            <div className="h-4 w-1 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
            <div className="h-4 w-20 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
            <div className="h-4 w-1 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
            <div className="h-4 w-32 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" aria-hidden="true">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Gallery Skeleton */}
            <div className="bg-[#F7F5F0] rounded-lg overflow-hidden">
              {/* Main Image */}
              <div className="aspect-[16/10] bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse"></div>
              
              {/* Thumbnail Strip */}
              <div className="p-4 flex gap-2 overflow-hidden">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-20 h-16 flex-shrink-0 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"
                  ></div>
                ))}
              </div>
            </div>

            {/* Title Block Skeleton */}
            <div className="space-y-4">
              {/* H1 Title */}
              <div className="h-8 w-3/4 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
              
              {/* Badge Pills Row */}
              <div className="flex flex-wrap gap-2">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="h-6 w-24 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded-full"
                  ></div>
                ))}
              </div>

              {/* Price */}
              <div className="h-10 w-40 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>

              {/* Trust Icons Row */}
              <div className="flex items-center gap-4">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2"
                  >
                    <div className="w-5 h-5 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded-full"></div>
                    <div className="h-4 w-20 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Specs Grid Skeleton */}
            <div className="bg-[#F7F5F0] rounded-lg p-6">
              <div className="h-6 w-32 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded mb-4"></div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded-full"></div>
                    <div className="h-3 w-16 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
                    <div className="h-5 w-20 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Features List Skeleton */}
            <div className="bg-[#F7F5F0] rounded-lg p-6">
              <div className="h-6 w-40 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded mb-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
                    <div className="h-4 w-32 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Description Card Skeleton */}
            <div className="bg-[#F7F5F0] rounded-lg p-6">
              <div className="h-6 w-36 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded mb-4"></div>
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="h-4 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"
                    style={{ width: i === 5 ? '60%' : '100%' }}
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* Desktop Sidebar Skeleton */}
          <div className="hidden lg:block space-y-6">
            {/* Price Card */}
            <div className="bg-[#F7F5F0] rounded-lg p-6 sticky top-24 space-y-4">
              {/* Price */}
              <div className="h-8 w-32 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
              
              {/* Payment Estimate */}
              <div className="h-5 w-40 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>

              {/* Tab Buttons */}
              <div className="flex gap-2">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="h-10 flex-1 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded-lg"
                  ></div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3">
                <div className="h-12 w-full bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded-lg"></div>
                <div className="h-12 w-full bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded-lg"></div>
              </div>

              {/* Quick Action Rows */}
              <div className="space-y-3 pt-4 border-t border-[#E8E2D6]">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-5 h-5 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
                    <div className="h-4 w-28 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
                  </div>
                ))}
              </div>

              {/* Trust Badges */}
              <div className="pt-4 border-t border-[#E8E2D6] space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded-full"></div>
                    <div className="h-3 w-32 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Bottom Bar Skeleton */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E8E2D6] p-4 shadow-lg z-40" aria-hidden="true">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="h-4 w-20 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
            <div className="h-6 w-28 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded"></div>
          </div>
          <div className="flex gap-2">
            <div className="h-12 w-12 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded-lg"></div>
            <div className="h-12 w-32 bg-gradient-to-r from-[#F0EDE6] via-[#E8E2D6] to-[#F0EDE6] animate-pulse rounded-lg"></div>
          </div>
        </div>
      </div>
    </div>
  );
}